import { useCallback, useEffect, useState } from 'react';
import { Combobox } from '@/components/ui/combobox';

type ContactHit = {
  displayName: string;
  emails?: { value: string; primary?: boolean }[];
};

function withToken(init: RequestInit = {}): RequestInit {
  const token = localStorage.getItem('accessToken');
  return {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  };
}

function primaryEmail(c: ContactHit): string | undefined {
  const emails = c.emails ?? [];
  return emails.find((e) => e.primary)?.value ?? emails[0]?.value;
}

interface Props {
  value: string;
  onValueChange: (email: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function GoogleContactRecipientCombobox({
  value,
  onValueChange,
  disabled,
  placeholder = 'Cerca in rubrica Google…',
}: Props) {
  const [options, setOptions] = useState<{ value: string; label: string; description?: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchContacts = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `/api/gmail/contacts/search?q=${encodeURIComponent(q.trim())}`,
        withToken()
      );
      const data = await res.json();
      if (data.success && data.data?.contacts) {
        const opts = (data.data.contacts as ContactHit[])
          .map((c) => {
            const email = primaryEmail(c);
            if (!email) return null;
            return {
              value: email,
              label: c.displayName,
              description: email,
            };
          })
          .filter(Boolean) as { value: string; label: string; description?: string }[];
        setOptions(opts);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const displayOptions =
    value && !options.some((o) => o.value === value)
      ? [{ value, label: value, description: value }, ...options]
      : options;

  return (
    <Combobox
      options={displayOptions}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      searchPlaceholder="Digita almeno 2 caratteri…"
      emptyMessage={loading ? 'Ricerca…' : 'Nessun contatto in rubrica'}
      disabled={disabled}
      disableLocalFilter
      onSearchChange={(q) => {
        void fetchContacts(q);
      }}
    />
  );
}
